import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';

// Define category data structure
interface CategoryData {
  title: string;
  image: string;
  description: string;
  products: Array<{
    name: string;
    image?: string;
  }>;
}

// Map of category slugs to their data
const categoryData: Record<string, CategoryData> = {
  'banana-bread': {
    title: 'BANANA BREAD',
    image: '/bnanana_bread.jpg',
    description: 'Our delicious banana bread is made with ripe bananas and a perfect blend of spices.',
    products: [
      { name: 'Nutella Banana Bread', image: '/Nutella_Banana_Bread.png' },
      { name: 'Classic Banana Bread', image: '/Classic_banana_bread.png' },
      { name: 'Walnut Banana Bread', image: '/Walnut_Banana_Bread.png' },
      { name: 'Chocolate Chips Banana Bread', image: '/Chocolate_Chips_Banana_Bread.png' }
    ]
  },
  'pastries': {
    title: 'PASTRIES',
    image: '/pastries.jpg',
    description: 'Flaky, buttery pastries made fresh daily with premium ingredients.',
    products: [
      { name: 'Nutella Belgium Pastry', image: '/Nutella_Belgium_Pastry.png' },
      { name: 'Cadbury Chocolate Pastry', image: '/Cadbury_Chocolate_Pastry.png' }
    ]
  },
  'donuts': {
    title: 'DONUTS',
    image: '/donuts.jpg',
    description: 'Soft, fluffy donuts with a variety of glazes and toppings to satisfy your sweet tooth.',
    products: [
      { name: 'Sniker Donut', image: '/Sniker_Donut.png' },
      { name: 'Oreo Donut', image: '/Oreo_Donut.png' },
      { name: 'Red velvet Donut', image: '/Red_velvet_Donut.png' },
      { name: 'Lotus Donut', image: '/Lotus_Donut.png' }
    ]
  },
  'puff-pastries': {
    title: 'PUFF PASTRIES',
    image: '/Puff_pastries.jpg',
    description: 'Light and airy puff pastries with delicate layers and rich fillings.',
    products: [
      { name: 'Chicken bread', image: '/chicken_bread_puff_pastery.png' }
    ]
  },
  'sundaes': {
    title: 'SUNDAES',
    image: '/Sundae.png',
    description: 'Decadent ice cream sundaes topped with homemade sauces and premium toppings.',
    products: [
      { name: 'Lotus', image: '/Lotus_sundae.png' },
      { name: 'Red velvet', image: '/Red_velvet_sundae.png' },
      { name: 'Chocolate', image: '/chocolate_sundae.png' }
    ]
  },
  'cakes': {
    title: 'CAKES',
    image: '/cake.jpg',
    description: 'Beautiful, moist cakes for any occasion, made with love and attention to detail.',
    products: [
      { name: 'Strawberry Cake', image: '/Strawberry_Cake.png' },
      { name: 'Red Velvet Cake', image: '/Red_VelvetCake.png' },
      { name: 'Oreo Cake', image: '/Oreo_Cake.png' },
      { name: 'Three Milk Cake', image: '/_ThreeMilk_Cake.png' },
      { name: 'Lotus Cake', image: '/Lotus_Cake.png' },
      { name: 'Cadbury Chocolate Cake', image: '/Cadbury_Chocolate_Cake.png' },
      { name: 'Caramel Cruch Cake', image: '/Caramel_Cruch_Cake.png' },
      { name: 'Customise Buttercream Cake', image: '/Customize_ButterCreamcake.png' },
      { name: 'German Fudge Cake', image: '/GermanFudge_Cake.png'  },
      { name: 'Blueberry Cake', image: '/Blueberry_Cake.png' },
      { name: 'Customize Whipped Cream Cake', image: '/CustomizeWhippedCreamCake.png' }
    ]
  },
  'cupcakes': {
    title: 'CUPCAKES',
    image: '/cupcake.jpg',
    description: 'Perfectly portioned cupcakes with fluffy frosting and creative decorations.',
    products: [
      { name: 'Red Velvet Cupcake', image: '/RedVelvet_Cupcake .png' },
      { name: 'Chocolate Cupcake', image: '/Chocolate_Cupcake .png' },
      { name: 'Vanilla Buttercream', image: '/Vanilla_ButtercreamCupcake.png' },
      { name: 'Customize butter cream cupcake', image: '/Customize_buttercream_cupcake.png' },
      { name: 'Customize whipped cream cupcake', image: '/whippedcream_cupcake.png' }
    ]
  },
  'fondant': {
    title: 'FONDANT',
    image: '/Fondant.jpg',
    description: 'Elegant fondant creations that transform cakes into works of art.',
    products: [
      { name: 'Fondant Cake', image: '/FondantCake.png' },
      { name: 'Customize Fondant Cake' , image: '/Customized_Fondant.png'}
    ]
  },
  'brownies': {
    title: 'BROWNIES',
    image: '/brownies.jpg',
    description: 'Rich, fudgy brownies that are the perfect balance of chewy and chocolatey.',
    products: [
      { name: 'Nutella Fudge Brownie', image: '/NutellaFudge_Brownie.png' },
      { name: 'Classic Chocolate Fudge Brownie', image: '/FudgeBrownie.png'  },
      { name: 'Salted Caramel Brownie', image: '/SaltedCaramel_Brownie.png'  }
    ]
  },
  'cookies': {
    title: 'COOKIES',
    image: '/cookies.jpg',
    description: 'Freshly baked cookies in a variety of flavors, from classic chocolate chip to unique creations.',
    products: [
      { name: 'Hand Painted Sugar Cookie', image: '/Sugar_Cookie.png' },
      { name: 'New York Chunk Cookie', image: '/Chunk_Cookie.png' },
      { name: 'Lotus Cookie', image: '/_LotusCookies .png' }
    ]
  }
};

const CategoryHeader = styled.div`
  width: 100%;
  height: 180px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  margin: clamp(20px, 3vw, 36px) 0; /* equal spacing above and below */
`;

const CategoryTitle = styled.h1`
  color: var(--primary-purple);
  font-size: 4.25rem;
  text-align: center;
  width: 100%;
  font-family: 'Great Vibes', 'Betrycia-Regular', cursive;
  font-weight: 400;
  margin: 0;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 3.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.4rem;
  }
`;

const CategoryContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem; /* remove extra top padding so spacing below hero matches */
`;

const CategoryDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  text-align: center;
`;

const ProductsSection = styled.div`
  margin-top: clamp(20px, 3vw, 36px); /* match hero bottom spacing for perfect centering */
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
`;

const ProductCard = styled.div`
  background: var(--primary-purple);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  will-change: transform;
  border: 1px solid rgba(255, 255, 255, 0.12);
  display: flex;
  flex-direction: column;
  height: 320px;
  
  &:hover {
    transform: scale(1.03);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.2);
  }
`;

const ProductImage = styled.img.attrs({ loading: 'lazy', decoding: 'async' })`
  width: 100%;
  height: 240px;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);

  ${ProductCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProductName = styled.h3`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  padding: 0.6rem 0.8rem;
  text-align: center;
  font-size: 1.05rem;
  color: #fff;
  margin: 0;
  letter-spacing: 0.2px;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2rem;
  color: var(--primary-purple);
  margin-bottom: 1rem;
  font-family: 'Betrycia-Regular', serif;
`;

const OrderNowWrapper = styled.div`
  text-align: center;
  margin-top: clamp(28px, 5vw, 64px);
  margin-bottom: clamp(28px, 5vw, 64px); /* equal spacing above and below */
`;

const OrderNowButton = styled.a`
  display: inline-block;
  background-color: var(--primary-purple);
  color: white;
  padding: 0.9rem 1.6rem;
  border-radius: 12px; /* rectangular with slightly curved corners */
  text-decoration: none;
  font-weight: 700;
  letter-spacing: 0.2px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.20);
  }
`;

// Format a string to Title Case with lowercase remainder for each word
const formatTitle = (s: string) => {
  return s
    .split(/\s+/)
    .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
};

const CategoryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  // Add scroll-reveal logic so cards become visible on this page
  useEffect(() => {
    const elements = document.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLElement;
          const dir = el.getAttribute('data-reveal-dir');
          if (dir) {
            el.setAttribute('data-reveal', dir);
          }
          el.classList.add('revealed');
        }
      });
    }, { threshold: 0.15 });

    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const category = categoryData[slug || ''] || {
    title: 'Category Not Found',
    image: '/FlourAndFancy_logo.PNG',
    description: 'Sorry, this category does not exist.',
    products: []
  };

  return (
    <>
      <CategoryHeader>
        <CategoryTitle>{formatTitle(category.title)}</CategoryTitle>
      </CategoryHeader>
      
      <CategoryContent>
        {/* Category description removed as requested */}
        
        {category.products && category.products.length > 0 && (
          <ProductsSection>
            {/* Heading under paragraph removed as requested */}
            <ProductsGrid>
              {category.products.map((product, index) => (
                <ProductCard key={index}>
                  <ProductImage
                    src={product.image ? product.image : category.image}
                    alt={product.name}
                  />
                  <ProductName>{product.name}</ProductName>
                </ProductCard>
              ))}
            </ProductsGrid>
          </ProductsSection>
        )}
        
        <OrderNowWrapper>
          <OrderNowButton href="https://wa.me/923708025503">
            Order Now
          </OrderNowButton>
        </OrderNowWrapper>
      </CategoryContent>
    </>
  );
};

export default CategoryPage;